/**
 * Results Showcase Section
 * Features: Custom desktop carousel, Swiper mobile, Before/After slider
 */

class ResultsShowcase extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.currentIndex = 0;
    this.isDragging = false;
    this.dragStartX = 0;
  }

  connectedCallback() {
    this.cacheElements();
    this.bindEvents();
    this.initBeforeAfterSliders();
    this.observeVisibility();
  }

  disconnectedCallback() {
    this.destroy();
  }

  observeVisibility() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.observer.disconnect();
          this.observer = null;
          this.initCarousel();
        }
      });
    }, { rootMargin: '200px' });

    this.observer.observe(this);
  }

  initCarousel() {
    if (window.innerWidth >= 750) {
      this.initDesktopCarousel();
    } else {
      this.initSwiper();
    }
  }

  cacheElements() {
    this.container = this.querySelector('.results-showcase__container');
    this.carousel = this.querySelector('.results-showcase__carousel');
    this.carouselWrapper = this.querySelector('.results-showcase__carousel-wrapper');
    this.cards = this.querySelectorAll('.results-showcase__carousel .results-showcase__card');
    this.prevBtn = this.querySelector('.results-showcase__carousel .swiper-nav__arrow--prev');
    this.nextBtn = this.querySelector('.results-showcase__carousel .swiper-nav__arrow--next');
    this.dots = this.querySelectorAll('.results-showcase__carousel .swiper-pagination-bullet');
    this.swiperEl = this.querySelector('.results-showcase__swiper');
  }

  bindEvents() {
    window.addEventListener('resize', this.handleResize.bind(this));

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goToSlide(this.currentIndex - 1));
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goToSlide(this.currentIndex + 1));
    }

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    document.addEventListener('shopify:section:load', (e) => {
      if (e.target.contains(this)) {
        this.initCarousel();
      }
    });

    document.addEventListener('shopify:block:select', (e) => {
      if (e.target.closest('[data-section-type="results-showcase"]') === this) {
        const block = e.target;
        const index = Array.from(this.cards).indexOf(block.closest('.results-showcase__card'));
        if (index >= 0) {
          this.goToSlide(index);
        }
      }
    });
  }

  handleResize() {
    // Skip if observer hasn't triggered yet
    if (this.observer) return;

    if (window.innerWidth >= 750) {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.initDesktopCarousel();
    } else {
      if (!this.swiper && this.swiperEl) {
        this.initSwiper();
      }
    }
  }

  initDesktopCarousel() {
    if (!this.carouselWrapper || this.cards.length === 0) return;

    this.updateCarouselPosition();
    this.updateDots();
    this.equalizeReviewHeights();
    this.setMinHeight();
  }

  setMinHeight() {
    if (!this.carouselWrapper) return;

    // Reset min-height to get natural height
    this.carouselWrapper.style.minHeight = '';

    // Wait for images to load and layout to settle
    requestAnimationFrame(() => {
      const height = this.carouselWrapper.offsetHeight;
      if (height > 0) {
        this.carouselWrapper.style.minHeight = `${height}px`;
      }
    });
  }

  equalizeReviewHeights() {
    const reviews = this.querySelectorAll('.results-showcase__carousel .results-showcase__review');
    if (reviews.length === 0) return;

    // Reset heights first
    reviews.forEach(review => {
      review.style.minHeight = '';
    });

    // Calculate max height
    requestAnimationFrame(() => {
      let maxHeight = 0;
      reviews.forEach(review => {
        const height = review.offsetHeight;
        if (height > maxHeight) {
          maxHeight = height;
        }
      });

      // Apply max height to all
      if (maxHeight > 0) {
        reviews.forEach(review => {
          review.style.minHeight = `${maxHeight}px`;
        });
      }
    });
  }

  goToSlide(index) {
    if (index < 0) index = this.cards.length - 1;
    if (index >= this.cards.length) index = 0;

    this.currentIndex = index;
    this.updateCarouselPosition();
    this.updateDots();
    this.updateActiveCard();
  }

  updateCarouselPosition() {
    if (!this.carouselWrapper) return;

    const activeWidth = 472;
    const inactiveWidth = 394;
    const gap = 18;

    let translateX = 0;
    for (let i = 0; i < this.currentIndex; i++) {
      translateX += inactiveWidth + gap;
    }

    this.carouselWrapper.style.transform = `translateX(-${translateX}px)`;
  }

  updateActiveCard() {
    this.cards.forEach((card, index) => {
      card.classList.toggle('results-showcase__card--active', index === this.currentIndex);
    });
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('swiper-pagination-bullet-active', index === this.currentIndex);
    });
  }

  initSwiper() {
    if (!this.swiperEl || typeof Swiper === 'undefined') return;

    this.swiper = new Swiper(this.swiperEl, {
      slidesPerView: 1,
      spaceBetween: 18,
      grabCursor: true,
      navigation: {
        nextEl: this.querySelector('.results-showcase__swiper-nav .swiper-nav__arrow--next'),
        prevEl: this.querySelector('.results-showcase__swiper-nav .swiper-nav__arrow--prev')
      },
      pagination: {
        el: this.querySelector('.results-showcase__swiper-nav .swiper-nav__pagination'),
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active'
      },
      breakpoints: {
        375: {
          slidesPerView: 1.05
        }
      }
    });
  }

  initBeforeAfterSliders() {
    const imageContainers = this.querySelectorAll('.results-showcase__image-container');

    imageContainers.forEach((container) => {
      const handle = container.querySelector('.results-showcase__slider-handle');
      const line = container.querySelector('.results-showcase__slider-line');
      const beforeImage = container.querySelector('.results-showcase__before-image');
      const afterImage = container.querySelector('.results-showcase__after-image');

      if (!handle || !line || !beforeImage || !afterImage) return;

      const state = {
        isDragging: false,
        position: 50
      };

      const updatePosition = (percentage) => {
        state.position = Math.max(0, Math.min(100, percentage));
        const pos = `${state.position}%`;

        handle.style.left = pos;
        line.style.left = pos;
        beforeImage.style.clipPath = `polygon(0% 0%, ${pos} 0%, ${pos} 100%, 0% 100%)`;
        afterImage.style.clipPath = `polygon(${pos} 0%, 100% 0%, 100% 100%, ${pos} 100%)`;
      };

      const onStart = (e) => {
        state.isDragging = true;
        e.preventDefault();

        if (this.swiper) {
          this.swiper.allowTouchMove = false;
        }
      };

      const onMove = (e) => {
        if (!state.isDragging) return;

        const rect = container.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const x = clientX - rect.left;
        const percentage = (x / rect.width) * 100;

        updatePosition(percentage);
        e.preventDefault();
      };

      const onEnd = () => {
        if (!state.isDragging) return;

        state.isDragging = false;

        if (this.swiper) {
          this.swiper.allowTouchMove = true;
        }
      };

      handle.addEventListener('mousedown', onStart);
      handle.addEventListener('touchstart', onStart, { passive: false });

      line.addEventListener('mousedown', onStart);
      line.addEventListener('touchstart', onStart, { passive: false });

      document.addEventListener('mousemove', onMove);
      document.addEventListener('touchmove', onMove, { passive: false });

      document.addEventListener('mouseup', onEnd);
      document.addEventListener('touchend', onEnd);

      updatePosition(50);
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

if (!customElements.get('results-showcase')) {
  customElements.define('results-showcase', ResultsShowcase);
}
